import { useQueryClient } from '@tanstack/react-query';
import { CreateArcgisCivil3dSettingConfigurationApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/create-arcgis-civil3d-setting-configuration.request';
import { UpdateArcgisCivil3dSettingConfigurationApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/update-arcgis-civil3d-setting-configuration.request';
import { DeleteArcgisCivil3dSettingConfigurationApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/delete-arcgis-civil3d-setting-configuration.request';
import { DuplicateArcgisCivil3dSettingConfigurationsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/duplicate-arcgis-civil3d-setting-configurations.request';
import { GetArcgisCivil3dSettingConfigurationApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/get-arcgis-civil3d-setting-configuration.request';
import { SearchArcgisCivil3dSettingConfigurationsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/search-arcgis-civil3d-setting-configurations.request';
import { ArcgisCivil3dSettingConfigurationApiModel } from '../../../../types/models/arcgis-civil3d-setting-configuration.model';
import {
  createSettingConfiguration,
  deleteSettingConfiguration,
  duplicateSettingConfigurations,
  exportSettingConfiguration,
  getSettingConfiguration,
  searchSettingConfigurations,
  updateSettingConfiguration,
} from '../../api/arcgis-civil3d-setting-configuration.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { ExportArcgisCivil3dSettingConfigurationApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/configurations/export-arcgis-civil3d-setting-configuration.request';

const baseQueryKey = 'setting-configuration';
const searchQueryKey = 'search';

export const useCreateSettingConfiguration = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateArcgisCivil3dSettingConfigurationApiRequest) =>
      (await createSettingConfiguration(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, settingConfiguration: data }] as const;
};

export const useUpdateSettingConfiguration = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateArcgisCivil3dSettingConfigurationApiRequest) =>
      (await updateSettingConfiguration(params)).data,
    onSuccess: (settingConfiguration) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey ||
            (queryKey[1] as GetArcgisCivil3dSettingConfigurationApiRequest)?.settingConfigurationId ===
              settingConfiguration.id),
      }),
  });
  return [request, { ...result, settingConfiguration: data }] as const;
};

export const useGetSettingConfiguration = <T extends ArcgisCivil3dSettingConfigurationApiModel>(
  params: GetArcgisCivil3dSettingConfigurationApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getSettingConfiguration<T>(params)).data,
    enabled: Boolean(params.settingConfigurationId && !Number.isNaN(params.settingConfigurationId)),
  });
  return { ...result, settingConfiguration: data } as const;
};

export const useDuplicateSettingConfigurations = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DuplicateArcgisCivil3dSettingConfigurationsApiRequest) =>
      (await duplicateSettingConfigurations(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useSearchSettingConfigurations = (params: SearchArcgisCivil3dSettingConfigurationsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchSettingConfigurations({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteSettingConfiguration = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteArcgisCivil3dSettingConfigurationApiRequest) =>
      (await deleteSettingConfiguration(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useExportSettingConfiguration = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: ExportArcgisCivil3dSettingConfigurationApiRequest) =>
      (await exportSettingConfiguration(params)).data,
  });
  return [request, result] as const;
};
